import { ThemeProvider } from "@zendeskgarden/react-theming";
import {ReactElement, useContext, useEffect, useMemo, useRef, useState} from "react";
import { ViziblyTheme } from "../analyst/ViziblyZDGTheme";
import AdminHeader from "../admin/AdminHeader";
import * as css from "./styles/executiveSummaryReportManagement.module.scss";
import useAppStore from "../../hooks/useAppStore";

import { Button } from "@zendeskgarden/react-buttons";

import "native-injects";
import Card from "../simplified-revenue/components/Card";
import { GetReportTablesQuery, ReportTableViewModel, useAddReportTableMutation, useDeleteReportTableMutation, useGetBrandingInformationQuery, useGetClientConfigLazyQuery, useGetReportTablesLazyQuery, useMoveReportTableMutation, useSetBrandingInformationMutation, useSetClientConfigMutation, useSetReportTableAccountsMutation, useUpdateReportTableMutation } from "../../__generated__/generated_types";
import { ReactComponent as MoveUpIcon } from '@zendeskgarden/svg-icons/src/16/chevron-up-stroke.svg';
import { ReactComponent as MoveDownIcon } from '@zendeskgarden/svg-icons/src/16/chevron-down-stroke.svg';
import { ReactComponent as PencilIcon } from '@zendeskgarden/svg-icons/src/16/pencil-stroke.svg';
import { ReactComponent as XIcon } from '@zendeskgarden/svg-icons/src/16/x-stroke.svg';
import { ReactComponent as PlusIcon } from '@zendeskgarden/svg-icons/src/16/plus-stroke.svg';
import { EditReportTable } from "./components/EditReportTable";
import DeleteReportTableModal from "./components/DeleteReportTableModal";
import { buildChartOfAccountsFlatComponentsBottomLine, FinancialEntity, useChartOfAccounts } from "../../contexts/chartofaccounts/ChartOfAccountsContext";
import AccountsSelector from "./components/AccountsSelector";
import {ExecutiveSummaryBrandingForm} from "./components/ExecutiveSummaryBrandingForm";
import {AuthContext} from "../../contexts/AuthContext";
import { Field, Hint, Label, Toggle } from "@zendeskgarden/react-forms";
import { toast } from "react-toastify";
import {EXECUTIVE_SUMMARY_DEFAULT_PRIMARY_COLOR, EXECUTIVE_SUMMARY_DEFAULT_SECONDARY_COLOR} from "../property-executive-summary/constants";


export default function ExecutiveSummaryReportManagement(): ReactElement {
    const appStore = useAppStore();
    const auth = useContext(AuthContext);
    const {user} = useContext(AuthContext);

    const signOffTableCreationAttempted = useRef<boolean>(false);

    const {data: brandingInformation, loading: brandingInformationLoading} = useGetBrandingInformationQuery({fetchPolicy: "no-cache"});
    const [getGetReportTables, {data: reportTablesData}] = useGetReportTablesLazyQuery({fetchPolicy: "no-cache"});
    const [deleteReportTable] = useDeleteReportTableMutation({notifyOnNetworkStatusChange: true});
    const [updateReportTable] = useUpdateReportTableMutation({notifyOnNetworkStatusChange: true});
    const [moveReportTable] = useMoveReportTableMutation({notifyOnNetworkStatusChange: true});
    const [addReportTable, {loading: addingTable}] = useAddReportTableMutation({notifyOnNetworkStatusChange: true});
    const [setReportTableAccounts] = useSetReportTableAccountsMutation({notifyOnNetworkStatusChange: true});
    const [getClientConfig, {data: clientConfig}] = useGetClientConfigLazyQuery({fetchPolicy: "no-cache"});
    const [setClientConfig, {loading: savingClientConfig}] = useSetClientConfigMutation({notifyOnNetworkStatusChange: true});
    const [setBrandingInformation, {loading: savingShowSignOffPage}] = useSetBrandingInformationMutation({notifyOnNetworkStatusChange: true});
    const coa = useChartOfAccounts();

    const [reportTables, setReportTables] = useState<GetReportTablesQuery["getReportTables"]>([]);
    const [signOffPageReportTable, setSignOffPageReportTable] = useState<ReportTableViewModel>();
    const [updateReportTableData, setUpdateReportTableData] = useState<{id: string, name: string}>();
    const [deleteReportTableData, setDeleteReportTableData] = useState<{id: string, name: string}>();
    const [addReportTableData, setAddReportTableData] = useState(false);
    const [showNotesForGuestUsers, setShowNotesForGuestUsers] = useState<boolean>(true);
    const [preventCommentDeletion, setPreventCommentDeletion] = useState<boolean>(false);
    const [showSignOffPage, setShowSignOffPage] = useState(false);

    function fetchReportTablesData() {
        getGetReportTables({variables: {budgetYear: auth.budgetYear}});
    }

    function handleUpdateReportTable(
        reportTableId: string,
        name: string | undefined
    ) {
        const promises = [];
        if (name !== undefined) {
            promises.push(new Promise((resolve, reject) => {
                updateReportTable({
                    variables: {
                        reportTableId: reportTableId,
                        budgetYear: auth.budgetYear,
                        newName: name
                    }
                })
                .then(() => resolve(true), () => reject())
                .catch(() => reject());
            }));
        }
        if (promises.length > 0) {
            Promise.all(promises)
            .then(
                () => {
                    setUpdateReportTableData(undefined);
                    fetchReportTablesData();
                }
            );
        }
    }

    function handleDeleteReportTable(reportTableId: string) {
        deleteReportTable({
            variables: {
                budgetYear: auth.budgetYear,
                reportTableId: reportTableId
            }
        })
        .then(
            () => {
                setDeleteReportTableData(undefined);
                fetchReportTablesData();
            }
        );
    }

    function handleAddReportTable(name: string | undefined) {
        if (!name) {
            return;
        }
        addReportTable({
            variables: {
                budgetYear: auth.budgetYear,
                reportTableName: name
            }
        })
        .then(
            () => {
                setAddReportTableData(false);
                fetchReportTablesData();
            }
        );
    }

    function handleMoveTable(reportTableId: string, direction: "up" | "down") {
        let afterTableId: string | null = null;
        const tblIndex = reportTables.findIndex(tbl => tbl.id === reportTableId);
        if (direction === "up") {
            if (tblIndex - 2 > -1) {
                afterTableId = reportTables[tblIndex - 2]?.id ?? null;
            }
        }
        else if (direction === "down") {
            if (tblIndex + 1 < reportTables.length) {
                afterTableId = reportTables[tblIndex + 1]?.id ?? null;
            }
        }
        moveReportTable({
            variables: {
                budgetYear: auth.budgetYear,
                reportTableId: reportTableId,
                afterTableId: afterTableId
            }
        })
        .then(
            () => {
                fetchReportTablesData();
            }
        );
    }

    function handleSetTableAccounts(reportTableId: string, accountIds: string[]) {
        setReportTableAccounts({
            variables: {
                budgetYear: auth.budgetYear,
                reportTableId: reportTableId,
                accountIds: accountIds ?? []
            }
        });
    }

    const chartOfAccountsFlat = useMemo<FinancialEntity[] | undefined>(() => {
        if (!coa.chartOfAccounts) {
            return undefined;
        }
        return buildChartOfAccountsFlatComponentsBottomLine(coa.chartOfAccounts);
    }, [coa.chartOfAccounts]);

    const topLevelAccounts = useMemo<FinancialEntity[] | undefined>(() => {
        if (!chartOfAccountsFlat) {
            return undefined;
        }
        return chartOfAccountsFlat.filter(x => x.parentId === null || x.parentId === undefined);
    }, [chartOfAccountsFlat]);

    useEffect(
        () => {
            appStore.set({ isLoading: false });
            getClientConfig();
            return () => undefined;
        },
        []
    );

    useEffect(() => {
        fetchReportTablesData();
        signOffTableCreationAttempted.current = false;
    }, [auth.budgetYear]);

    useEffect(() => {
        if (!reportTablesData) {
            return;
        }

        const regularReportTables = [...reportTablesData.getReportTables.filter((x) => !x.isSignOffPage)];
        const signOffTable = reportTablesData.getReportTables.find((x) => x.isSignOffPage);

        setReportTables(regularReportTables.sortBy("order"));
        setSignOffPageReportTable(signOffTable);

        if (signOffTable || topLevelAccounts === undefined || signOffTableCreationAttempted.current) {
            return;
        }

        // on load or data change, check if we need to create a table for the sign off page
        // only one should exist per client_budgetYear, and setting the name to BUDGET__SIGN__OFF__PAGE__TABLE
        // makes me feel pretty confident we won't run into a name collision with user defined tables
        signOffTableCreationAttempted.current = true;
        addReportTable({
            variables: {
                budgetYear: auth.budgetYear,
                reportTableName: "BUDGET__SIGN__OFF__PAGE__TABLE",
                isSignOffPage: true,
                accountIds: topLevelAccounts.map(x => x.id),
            },
        }).then(() => {
            fetchReportTablesData();
        });
    }, [reportTablesData, auth.budgetYear, topLevelAccounts]);

    useEffect(() => {
        if (clientConfig?.getClientConfig) {
            setShowNotesForGuestUsers(clientConfig.getClientConfig.showNotesForGuestUsers);
            setPreventCommentDeletion(clientConfig.getClientConfig.preventCommentDeletion);
        }
    }, [clientConfig]);

    useEffect(() => {
        const brandingInfo = brandingInformation?.getBrandingInformationAllowNulls;
        if (brandingInfo) {
            setShowSignOffPage(brandingInfo.showSignOffPage);
        }
    }, [brandingInformation]);

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <div className={css.wrapper}>
                <AdminHeader
                    title={"Executive Summary Report Management"}
                    subtitle={"Manage financial summary report tables."}
                />
                {user && user.email.endsWith("@vizibly.io") &&
                    <Card title={"Branding"} className={css.cardWrapper}>
                        <ExecutiveSummaryBrandingForm brandingInformation={brandingInformation} brandingInformationLoading={brandingInformationLoading} />
                    </Card>
                }
                <Card
                    title={"Report Tables"}
                    className={css.cardWrapper}
                    actions={
                        <>
                            <Button
                                isBasic
                                onClick={() => setAddReportTableData(true)}
                            >
                                <PlusIcon />
                            </Button>
                        </>
                    }
                >
                    <div className={css.reportTablesTableWrapper}>
                        <table className={css.reportTablesTable}>
                            <tbody>
                                {reportTables.map((tbl, index) => {
                                    return (
                                        <tr key={tbl.id}>
                                            <td className={`${css.cell}`}>
                                                <div className={css.content}>
                                                    { tbl.name }
                                                    <div className={css.controls}>
                                                        <Button
                                                            isBasic
                                                            onClick={() => setUpdateReportTableData(tbl)}
                                                        >
                                                            <PencilIcon />
                                                        </Button>
                                                        <Button
                                                            isBasic
                                                            isDanger
                                                            onClick={() => setDeleteReportTableData(tbl)}
                                                        >
                                                            <XIcon />
                                                        </Button>
                                                        <Button
                                                            isBasic
                                                            disabled={index === 0}
                                                            onClick={() => handleMoveTable(tbl.id, "up")}
                                                        >
                                                            <MoveUpIcon />
                                                        </Button>
                                                        <Button
                                                            isBasic
                                                            disabled={index === reportTables.length - 1}
                                                            onClick={() => handleMoveTable(tbl.id, "down")}
                                                        >
                                                            <MoveDownIcon />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className={css.cell}>
                                                {chartOfAccountsFlat &&
                                                <AccountsSelector
                                                    allAccounts={chartOfAccountsFlat}
                                                    selectedAccountIds={tbl.accounts.map(acc => acc.accountId)}
                                                    onChangeCallback={(accountIds) => handleSetTableAccounts(tbl.id, accountIds)}
                                                />
                                                }
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>

                <Card
                    title={"Sign-Off Page"}
                    className={css.cardWrapper}
                    actions={
                        <Field>
                            <Toggle
                                checked={showSignOffPage}
                                onChange={(evt) => {
                                    setShowSignOffPage(evt.target.checked);
                                    // these two are required fields for the mutation, so just asserting them here
                                    const primaryColor = brandingInformation?.getBrandingInformationAllowNulls?.primaryColor ?? EXECUTIVE_SUMMARY_DEFAULT_PRIMARY_COLOR;
                                    const secondaryColor = brandingInformation?.getBrandingInformationAllowNulls?.secondaryColor ?? EXECUTIVE_SUMMARY_DEFAULT_SECONDARY_COLOR;
                                    setBrandingInformation({
                                        variables: {
                                            input: {
                                                primaryColor,
                                                secondaryColor,
                                                showSignOffPage: evt.target.checked,
                                            }
                                        }
                                    });
                                }}
                                disabled={addingTable || savingShowSignOffPage}
                                aria-label="toggle showing the sign off page in report"
                            >
                                <Label>
                                    Enable
                                </Label>
                            </Toggle>
                        </Field>
                    }
                >
                    <div className={css.reportTablesTableWrapper}>
                        <p className={css.cardDescription}>
                            Add a page to the end for approval signatures. By default, it displays your top-level category rollups and their Budget year totals.
                        </p>
                        {showSignOffPage && signOffPageReportTable &&
                            <table className={css.reportTablesTable}>
                                <tbody>
                                    <tr>
                                        <td className={`${css.cell}`}>
                                            <div className={css.content}>
                                                Summary
                                            </div>
                                        </td>
                                        <td className={css.cell}>
                                            {chartOfAccountsFlat &&
                                                <AccountsSelector
                                                    allAccounts={chartOfAccountsFlat}
                                                    selectedAccountIds={signOffPageReportTable.accounts.map(acc => acc.accountId).sort()}
                                                    onChangeCallback={(accountIds) => {
                                                        if (accountIds.length <= 20) {
                                                            handleSetTableAccounts(signOffPageReportTable.id, accountIds);
                                                        }
                                                    }}
                                                    onValidate={(selectedAccounts) => selectedAccounts.length > 20 ? `Max of 20 Accounts allowed, please remove ${selectedAccounts.length - 20}`  : undefined}
                                                />
                                            }
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        }
                    </div>
                </Card>

                <Card
                    title={"Share Settings"}
                >
                    <div style={{padding: "1em"}}>
                        <Field>
                            <Toggle
                                checked={showNotesForGuestUsers}
                                disabled={savingClientConfig}
                                onChange={(evt) => {
                                    setClientConfig({
                                        variables: {
                                            configOptions: {
                                                showNotesForGuestUsers: evt.target.checked,
                                            }
                                        }
                                    }).then(({data}) => {
                                        if (data) {
                                            setShowNotesForGuestUsers(data.setClientConfig.showNotesForGuestUsers);
                                            toast.success("Note share settings saved");
                                        } else {
                                            toast.error("There was a problem, please try again later");
                                        }
                                    });
                                }}
                            >
                                <Label>Guests Can View Account Notes</Label>
                                <Hint>Account Notes can be shown or hidden, for Guest users. Comments from the Executive Summary Financials tab are not affected.</Hint>
                            </Toggle>
                        </Field>
                    </div>

                    <div style={{padding: "1em"}}>
                        <Field>
                            <Toggle
                                checked={preventCommentDeletion}
                                disabled={savingClientConfig}
                                onChange={(evt) => {
                                    setClientConfig({
                                        variables: {
                                            configOptions: {
                                                preventCommentDeletion: evt.target.checked,
                                            }
                                        }
                                    }).then(({data}) => {
                                        if (data) {
                                            setPreventCommentDeletion(data.setClientConfig.preventCommentDeletion);
                                            toast.success("Comment settings saved");
                                        } else {
                                            toast.error("There was a problem, please try again later");
                                        }
                                    });
                                }}
                            >
                                <Label>Prevent Comments From Being Deleted</Label>
                                <Hint>By default, the author may delete any comments left on shared executive summaries. Enabling this option will prevent anyone from removing comments, once they've been added.</Hint>
                            </Toggle>
                        </Field>
                    </div>
                </Card>
            </div>
            {
            updateReportTableData &&
            <EditReportTable
                existingReportTables={reportTablesData?.getReportTables ?? []}
                initialName={updateReportTableData.name}
                reportTableId={updateReportTableData.id}
                title="Edit Report Table"
                onCancel={() => setUpdateReportTableData(undefined)}
                onSubmit={(name) => handleUpdateReportTable(updateReportTableData.id, name)}
            />
            }
            {
            addReportTableData &&
            <EditReportTable
                existingReportTables={reportTablesData?.getReportTables ?? []}
                title="Add Report Table"
                onCancel={() => setAddReportTableData(false)}
                onSubmit={(name: string | undefined) => handleAddReportTable(name)}
            />
            }
            {deleteReportTableData && (
            <DeleteReportTableModal
                reportTableId={deleteReportTableData.id}
                reportTableName={deleteReportTableData.name}
                budgetYear={auth.budgetYear}
                onClose={() => {
                    setDeleteReportTableData(undefined);
                }}
                onConfirm={() => {
                    handleDeleteReportTable(deleteReportTableData.id);
                }}
            />
            )}
        </ThemeProvider>
    );
}